/**
 * Каркас приложения: вход, навигация, полоска синхронизации.
 *
 * Экранов пять (раздел 8). Нижняя панель показывает четыре — активная
 * тренировка открывается с экрана «Сегодня» и панель прячет: между
 * подходами промахнуться мимо кнопки нельзя.
 */

import { useEffect, useState } from 'react'
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom'

import { ApiError, NetworkError, api } from './api/client'
import type { AuthResult } from './api/types'
import { ensureSession, isMiniApp } from './auth/telegram'
import { hasToken, setToken } from './auth/session'
import {
  IconCalendar,
  IconChart,
  IconClock,
  IconDumbbell,
  IconTarget,
} from './components/icons'
import { Loader } from './components/ui'
import { startAutoSync, subscribe, retryFailed, type QueueState } from './offline/queue'
import Install from './screens/Install'
import Notifications from './screens/Notifications'
import Plan from './screens/Plan'
import Programs from './screens/Programs'
import SessionEdit from './screens/SessionEdit'
import Today from './screens/Today'
import Workout from './screens/Workout'
import Progress from './screens/Progress'
import Goals from './screens/Goals'
import History from './screens/History'

type AuthState = 'checking' | 'ready' | 'anonymous' | 'error'

function SyncBar() {
  const [state, setState] = useState<QueueState | null>(null)

  useEffect(() => subscribe(setState), [])
  useEffect(() => startAutoSync(), [])

  if (!state) return null
  if (state.failed > 0) {
    return (
      <div className="syncbar failed" onClick={() => void retryFailed()}>
        {state.failed} подх. не приняты сервером — нажми, чтобы повторить
      </div>
    )
  }
  if (state.pending > 0) {
    return (
      <div className="syncbar pending">
        {state.online ? 'Отправляю' : 'Нет связи'} · {state.pending} подх. ждут отправки
      </div>
    )
  }
  return null
}

function TabBar() {
  const tabs = [
    { to: '/', Icon: IconDumbbell, label: 'Сегодня', end: true },
    { to: '/plan', Icon: IconCalendar, label: 'Программа', end: false },
    { to: '/progress', Icon: IconChart, label: 'Прогресс', end: false },
    { to: '/goals', Icon: IconTarget, label: 'Цели', end: false },
    { to: '/history', Icon: IconClock, label: 'История', end: false },
  ]
  return (
    <nav className="tabbar" aria-label="Разделы">
      {tabs.map(({ to, Icon, label, end }) => (
        <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon size={24} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

/**
 * Что человек ввёл на экране входа.
 *
 * Поле одно, а входов два, и это не лень: бот присылает короткий код, но
 * ссылки, отправленные раньше, обязаны доработать свои пятнадцать минут.
 * Разбирать за человека, что у него в буфере, — работа приложения.
 */
export type LoginInput = { kind: 'code'; value: string } | { kind: 'token'; value: string }

/** Подписанный токен — это base64url и ровно одна точка, больше ничего. */
const TOKEN_SHAPE = /^[A-Za-z0-9_\-=]+\.[A-Za-z0-9_\-=]+$/

export function parseLoginInput(raw: string): LoginInput | null {
  let text = raw.trim()
  if (!text) return null

  // Сначала СРЕЗАЕМ обёртку, и только потом смотрим, что осталось. Обратный
  // порядок ломался на очевидном: в «https://app.regymo.ru/...» точки есть в
  // имени хоста, и любая ссылка уезжала бы в ветку подписанного токена.
  const marker = '/api/auth/magic/'
  const at = text.indexOf(marker)
  if (at >= 0) text = text.slice(at + marker.length)
  text = text.split(/[?#\s]/)[0] ?? ''
  if (text.includes('/')) text = text.slice(text.lastIndexOf('/') + 1)
  if (!text) return null

  // Форма токена проверяется до отправки, и не ради красоты: строка
  // подставляется в ПУТЬ запроса, а «..» или «/» в ней увели бы запрос на
  // соседнюю ручку.
  if (TOKEN_SHAPE.test(text)) return { kind: 'token', value: text }

  // Всё остальное уходит кодом КАК ЕСТЬ. Регистр, дефисы и подмену `O` на
  // ноль разбирает сервер (`backend/services/login_codes.py`): второй
  // алфавит на клиенте рано или поздно разошёлся бы с первым.
  return { kind: 'code', value: text }
}

/**
 * Экран для случая «открыли в браузере без токена».
 *
 * Здесь же вход по вставленной ссылке. Он нужен не для удобства: ярлык на
 * домашнем экране iOS держит СВОЁ хранилище, отдельное от Safari, и ссылку
 * из чата в него не открыть — iOS не умеет передавать ссылку установленному
 * веб-приложению. Без поля вставки в ярлык нельзя войти вообще.
 */
function NeedLogin({ onDone }: { onDone: () => void }) {
  const [paste, setPaste] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  async function submit(): Promise<void> {
    const input = parseLoginInput(paste)
    if (!input) {
      setFailure('Набери код из бота — он приходит на команду /login.')
      return
    }
    setBusy(true)
    setFailure('')
    try {
      const result =
        input.kind === 'code'
          ? await api.post<AuthResult>('/auth/code', { code: input.value }, { anonymous: true })
          : await api.get<AuthResult>(`/auth/magic/${input.value}`, { anonymous: true })
      if (typeof result?.token !== 'string' || !result.token) {
        throw new Error('Сервер ответил не тем, чего мы ждали. Попробуй ещё раз.')
      }
      setToken(result.token)

      // Перезагрузки здесь быть НЕ должно, и это не мелочь. В приватном
      // режиме Safari localStorage закрыт, и токен живёт только в памяти
      // вкладки: перезагрузка стёрла бы его вместе с ней, экран снова
      // попросил бы вход, а ссылка уже одноразово потрачена. Человек
      // крутился бы по кругу, сжигая ссылку за ссылкой.
      onDone()
    } catch (e) {
      setFailure(
        e instanceof NetworkError
          ? 'Нет связи с сервером. Код не потрачен — попробуй ещё раз.'
          : e instanceof ApiError
            ? e.message
            : 'Код не принят. Попроси новый командой /login.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="screen login">
      <div className="login-mark">
        <IconDumbbell size={44} />
      </div>
      <h1>Нужен вход</h1>
      <p className="muted" style={{ margin: 0 }}>
        Напиши боту команду <b>/login</b> — он пришлёт короткий код. Набери его здесь.
      </p>

      <input
        className="se-search"
        type="text"
        value={paste}
        onChange={(e) => setPaste(e.target.value)}
        onKeyDown={(e) => {
          // На iOS клавиатура показывает «Go», и нажать его естественнее,
          // чем тянуться к кнопке под полем.
          if (e.key === 'Enter' && !busy) void submit()
        }}
        placeholder="Код из бота"
        autoComplete="one-time-code"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
      />
      {failure && <div className="se-fail">{failure}</div>}
      <button className="btn wide primary" disabled={busy} onClick={() => void submit()}>
        {busy ? 'Вхожу…' : 'Войти'}
      </button>

      <p className="faint" style={{ marginTop: 16 }}>
        Код живёт несколько минут и срабатывает один раз. Регистр и дефис не важны.
        Ссылка из старых сообщений бота тоже подойдёт — вставь её целиком.
      </p>

      <Link className="btn wide ghost" to="/install">
        Как поставить на экран «Домой»
      </Link>
    </div>
  )
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>('checking')
  const [authError, setAuthError] = useState<string>('')
  const location = useLocation()

  useEffect(() => {
    let cancelled = false
    ensureSession()
      .then((ok) => {
        if (cancelled) return
        setAuth(ok ? 'ready' : 'anonymous')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setAuthError(e instanceof Error ? e.message : 'Не удалось войти')
        // В Mini App вход обязан был сработать — показываем причину.
        // В браузере это ожидаемо: там вход по ссылке из бота.
        setAuth(isMiniApp() ? 'error' : 'anonymous')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (auth === 'checking') {
    return (
      <div className="screen">
        <Loader label="Вхожу…" />
      </div>
    )
  }

  if (auth === 'error') {
    return (
      <div className="screen">
        <h1>Не удалось войти</h1>
        <div className="card" style={{ color: 'var(--danger)' }}>{authError}</div>
        <button className="btn wide" onClick={() => window.location.reload()}>
          Попробовать снова
        </button>
      </div>
    )
  }

  if (auth === 'anonymous' && !hasToken()) {
    // Инструкция по установке доступна и без входа — именно там она и нужна:
    // человек ставит ярлык до того, как войдёт. Всё остальное без токена
    // показывать нечего.
    return (
      <Routes>
        <Route path="/install" element={<Install />} />
        <Route path="*" element={<NeedLogin onDone={() => setAuth('ready')} />} />
      </Routes>
    )
  }

  // На активной тренировке панель убирается: экран занят подходами.
  const inWorkout = location.pathname.startsWith('/workout/')

  return (
    <>
      <SyncBar />
      <Routes>
        <Route path="/" element={<Today />} />
        <Route path="/workout/:sessionId" element={<Workout />} />
        <Route path="/plan" element={<Plan />} />
        <Route path="/plan/:sessionId" element={<SessionEdit />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/install" element={<Install />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/history" element={<History />} />
        <Route path="*" element={<Today />} />
      </Routes>
      {!inWorkout && <TabBar />}
    </>
  )
}
